import React, { useEffect, useMemo, useRef, useState } from 'react'

export type SuggestOption = {
  value: string
  label: string
  detail?: string
  keywords?: string
}

type SuggestInputProps = {
  value: string
  onChange: (value: string) => void
  options: SuggestOption[]
  placeholder?: string
  'aria-label'?: string
  disabled?: boolean
  id?: string
}

function optionHaystack(option: SuggestOption): string {
  return [option.value, option.label, option.detail, option.keywords]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

export function filterSuggestOptions(
  options: SuggestOption[],
  query: string,
  limit = 8,
): SuggestOption[] {
  const q = query.trim().toLowerCase()
  const matched = q
    ? options.filter((option) => optionHaystack(option).includes(q))
    : options
  return matched.slice(0, limit)
}

/** If the typed query uniquely matches an option, use that option's value. */
export function resolveSuggestValue(query: string, options: SuggestOption[]): string {
  const q = query.trim()
  if (!q) return ''
  const exact = options.find((option) => option.value.toLowerCase() === q.toLowerCase())
  if (exact) return exact.value
  const matches = filterSuggestOptions(options, q, 20)
  if (matches.length === 1) return matches[0].value
  return q
}

export default function SuggestInput({
  value,
  onChange,
  options,
  placeholder,
  'aria-label': ariaLabel,
  disabled = false,
  id,
}: SuggestInputProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)

  const matches = useMemo(
    () => (open ? filterSuggestOptions(options, value) : []),
    [open, options, value],
  )

  useEffect(() => {
    setHighlight(0)
  }, [value, open])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const pick = (option: SuggestOption) => {
    onChange(option.value)
    setOpen(false)
  }

  const showList = open && value.trim().length > 0 && matches.length > 0

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        className="input input-bordered w-full"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (!showList) {
            if (event.key === 'Escape') setOpen(false)
            return
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setHighlight((i) => Math.min(i + 1, matches.length - 1))
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlight((i) => Math.max(i - 1, 0))
          } else if (event.key === 'Enter') {
            event.preventDefault()
            const option = matches[highlight]
            if (option) pick(option)
          } else if (event.key === 'Escape') {
            event.preventDefault()
            setOpen(false)
          }
        }}
      />
      {showList ? (
        <ul
          className="menu menu-sm absolute z-30 mt-1 w-full rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
          role="listbox"
        >
          {matches.map((option, index) => (
            <li key={`${option.value}-${index}`} role="option" aria-selected={index === highlight}>
              <button
                type="button"
                className={index === highlight ? 'active' : ''}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
              >
                <span className="flex min-w-0 flex-col items-start">
                  <span className="truncate font-medium">{option.label}</span>
                  {option.detail ? (
                    <span className="truncate text-xs font-normal opacity-60">{option.detail}</span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
